import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NextPage, NextPageContext } from 'next';

import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import ErrorPage from '~/pages/_error';
import { getContentBlock } from '~/src/api/content/contentBlock';
import { addressCard, addressCardProps } from '~/src/constants/addressCard';
import { addressList, domainProps } from '~/src/constants/addressList';
import Layout from '~/src/features/_layout';
import DistrictLayout from '~/src/features/districts-layout';
import { initialProps } from '~/src/helpers/basePageFunc';
import { formattingTariffForCard } from '~/src/helpers/formatter';
import { getDistrictDeclination } from '~/src/helpers/getDistrictDeclination';
import { TState } from '~/src/store';
import { getBannerForInternet, getTariffsForInternet } from '~/src/store/actions/internet';
import { getQuizData, setTariffsForQuizAction } from '~/src/store/actions/quiz';
import { selectCompanyName } from '~/src/store/selectors/city';

const aliasForQuiz = 'internet';
const domains = addressList.map(item => item.domain);

interface DistrictPageProps {
    currentCity: ICurrentCity,
    cards: addressCardProps[],
    district?: domainProps,
    domain: string,
}

const DistrictPage: NextPage<DistrictPageProps> = ({ district, cards, domain, currentCity }) => {
    const tariffs = useSelector((state: TState) => state.internet.tariffs);
    const companyName = useSelector(selectCompanyName);

    const filteredTariffs = useMemo(() => {
        return tariffs.map(formattingTariffForCard);
    }, [tariffs]);

    if (district && domains.includes(domain)) {
        const { name } = district;
        const { name: cityName, cityIn } = currentCity;
        const prepositionalFull = getDistrictDeclination(name, 'prepositional_case');
        const prepositional = getDistrictDeclination(name, 'prepositional_case', false);
        const title = `Интернет-провайдер ${companyName} в ${prepositional} – ${cityName}`;
        const description = `Ищете интернет-провайдера по своему адресу в ${prepositional}?` +
            ` Проверьте адрес подключения к ${companyName} в ${prepositionalFull} в ${cityIn}.` +
            ' Выгодные тарифы Интернета и ТВ, бесплатная консультация.';
        return (
            <Layout
                title={title}
                description={description}
                withMenu={true}
                withFooter={true}
                withChat={true}
            >
                <DistrictLayout
                    district={district}
                    tariffs={filteredTariffs}
                    cards={cards}
                    currentCity={currentCity}
                />
            </Layout>
        );
    } else {
        return <ErrorPage statusCode={404} />;
    }
};

DistrictPage.getInitialProps = async(options: NextPageContext) => {
    await initialProps(options, { isNeedAuthData: true });
    const { query, store } = options;
    const state: TState = store.getState();
    const providerId = state.city.provider.providerId;
    const { currentCity } = state.city;
    const { domain } = currentCity;

    const districtsData = addressList.find((item) => item.domain === domain);

    const district = districtsData?.districts.find(item => item.url === query.district);
    const cards = addressCard;

    const [respContentBlock] = await Promise.all([
        getContentBlock(providerId, 'quiz_game'),
        store.dispatch(getBannerForInternet(providerId)),
        store.dispatch(getTariffsForInternet(providerId)),
    ]);

    if (respContentBlock.isSuccess) {
        const [pointEntry] = respContentBlock?.payload?.items;
        // @ts-ignore
        await store.dispatch(getQuizData(providerId, aliasForQuiz, pointEntry));
        store.dispatch(setTariffsForQuizAction(store.getState().internet.tariffs));
    }

    if (options.res && (!district || !domains.includes(domain))) {
        options.res.statusCode = 404;
    }

    return { district, cards, domain, currentCity };
};

export default DistrictPage;
